import React from 'react';
import { graphql } from 'gatsby';
import SEO from 'components/SEO';
import { Flex, Box, Container } from 'components/Grid';
import H1 from 'components/H1';
import H2 from 'components/H2';
import Text from 'components/Text';
import Image from 'components/Image';
import Animate from 'components/Animate';
import Paragraph from 'components/Paragraph';
import ProjectGridItem from 'components/ProjectGridItem';
import SectionNavigation from 'components/SectionNavigation';
import styled from 'styled-components';
import { animateScroll } from 'react-scroll';
import getSeo from 'utils/helpers/getSeo';

const AboutPageStyled = styled.div`
	.section-observe {
		min-height: 100vh;
		height: 100%;
	}
`;

const AboutPage = ({ data }) => {
	const seo = getSeo(data.content);

	const gatheredSections = () => {
		const contentTitles = data.content.contentBlocks.map((item, key) => {
			return {
				title: item.navigationTitle,
				index: key + 1
			};
		});

		return [
			{
				title: data.content.navigationTag,
				index: 0
			},
			...contentTitles
		];
	};

	const [activeSectionId, setActiveSectionId] = React.useState(0);

	const handleChange = entries => {
		entries.forEach(element => {
			if (element.isIntersecting && element.intersectionRatio > 0.5) {
				const i = +element.target.getAttribute('data-index');
				setActiveSectionId(i);
			} else {
			}
		});
	};

	const handleNavClick = (direction = '', id = null) => {
		console.log(id);

		let pos;
		const sectionsWrappers = document.querySelectorAll('.section-observe');

		const targetId = id !== null ? id : activeSectionId;

		if (sectionsWrappers) {
			const targetElement = Array.from(sectionsWrappers)[targetId];

			if (direction == 'down') {
				pos = targetElement.clientHeight + targetElement.offsetTop;
			} else if (direction == 'up') {
				pos = targetElement.clientHeight - targetElement.offsetTop;
			} else {
				pos = targetElement.offsetTop;
			}

			animateScroll.scrollTo(pos);
		}
	};

	React.useEffect(() => {
		const observer = new IntersectionObserver(handleChange, {
			threshold: [0, 0.25, 0.5, 0.75, 1]
		});

		const sectionsWrapper = document.querySelectorAll('.section-observe');

		sectionsWrapper.forEach(element => {
			observer.observe(element);
		});
	}, [activeSectionId]);

	return (
		<AboutPageStyled className="sections">
			<SectionNavigation
				bullets={gatheredSections()}
				handleNavClick={handleNavClick}
				activeBulletIndex={activeSectionId}
			/>
			<SEO {...seo} keywords={[`gatsby`, `application`, `react`]} />
			<Flex color="white" className="section-observe" data-index={0}>
				<Image bg objectFit="cover" fluid={data.content.heroImage.fluid} />
				<Container
					py={[6, 6, 3]}
					justifyContent="center"
					flexDirection="column"
					display="flex"
					px={[3, 4]}
				>
					<Animate animateOnInView={true}>
						<Flex flexWrap="wrap">
							<Box width={[1, 1, 1, 1 / 2]}>
								<H1 fontSize={['h2', 'h1']}>{data.content.headline}</H1>

								<Paragraph fontSize={['p', 'pLarge']}>{data.content.intro}</Paragraph>
							</Box>
							<Box width={[1, 1, 1, 1 / 2]}>
								<Text
									dangerouslySetInnerHTML={{
										__html: data.content.column1.childMarkdownRemark.html
									}}
								/>
							</Box>
						</Flex>
					</Animate>
				</Container>
			</Flex>

			{data.content.contentBlocks &&
				data.content.contentBlocks.map((item, key) => (
					<Flex
						data-index={key + 1}
						height="100vh"
						className="section-observe"
						id={item.anchorLink}
						py={[4, 6, 3]}
						px={[1, 2, 4]}
						key={item.id}
						bg={key % 2 == 0 ? 'client.accent' : ''}
					>
						<Container justifyContent="center" flexDirection="column" display="flex">
							<Animate animateOnInView={true}>
								<Flex
									alignItems="center"
									flexWrap="wrap"
									flexDirection={key % 2 == 1 ? 'row' : 'row-reverse'}
								>
									<Box textAlign="center" width={[1, 1, 1 / 2]} mb={[4, 0]}>
										<img src={item.icon.file.url} />
									</Box>
									<Box width={[1, 1, 1 / 2]}>
										<H2 color={key % 2 == 1 ? 'client.primary' : ''}>{item.title}</H2>
										<Paragraph fontSize={['p', 'pLarge']}>{item.intro.intro}</Paragraph>
										<Text
											dangerouslySetInnerHTML={{
												__html: item.text.childMarkdownRemark.html
											}}
										/>
									</Box>
								</Flex>
							</Animate>
						</Container>
					</Flex>
				))}
			<Flex grid gridTemplateColumns={['1fr', 'repeat(3, 1fr)']}>
				{data.content.featuredCase &&
					data.content.featuredCase.map(item => (
						<ProjectGridItem key={item.id} item={item} aspectRatio={1} />
					))}
			</Flex>
		</AboutPageStyled>
	);
};

export const query = graphql`
	query {
		content: contentfulPageAbout(contentful_id: { eq: "3x5Zf8IBlYUuxlBW7nc0k3" }) {
			headline
			seo {
				ogText
				ogDescription
				ogImage {
					file {
						url
					}
				}
			}
			intro
			navigationTag
			heroImage {
				fluid(maxWidth: 2400) {
					...GatsbyContentfulFluid
				}
			}
			column1 {
				column1
				childMarkdownRemark {
					html
					rawMarkdownBody
				}
			}

			contentBlocks {
				id
				anchorLink
				navigationTitle
				intro {
					intro
				}
				text {
					text
					childMarkdownRemark {
						html
						rawMarkdownBody
					}
				}
				icon {
					file {
						url
					}
				}
				title
			}
			featuredCase {
				headline
				id
				type: __typename
				darkText

				featuredImage {
					file {
						contentType
						url
					}
					fluid {
						...GatsbyContentfulFluid
					}
				}
				slug
			}
		}
	}
`;

export default AboutPage;
